/**
 * DNA Atendimento AI - Sistema de Atendimento Inteligente
 * Classificação, Respostas Automáticas e Gestão de Comentários/DMs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  MessageSquare, Mail, Send, RefreshCw, Filter, Clock,
  CheckCircle, XCircle, Archive, Sparkles, AlertTriangle,
  ThumbsUp, ThumbsDown, User, Instagram, MessageCircle,
  Zap, Copy, BarChart3, Inbox, Users, Heart
} from 'lucide-react';

const STATUS_CONFIG = {
  nao_lida: { label: 'Não Lida', color: 'bg-blue-500', icon: Inbox },
  em_analise: { label: 'Em Análise', color: 'bg-yellow-500', icon: Clock },
  respondida: { label: 'Respondida', color: 'bg-green-500', icon: CheckCircle },
  arquivada: { label: 'Arquivada', color: 'bg-gray-500', icon: Archive },
  pendente: { label: 'Pendente', color: 'bg-orange-500', icon: AlertTriangle }
};

const CATEGORIA_CONFIG = {
  matricula: { label: 'Matrícula', color: 'bg-purple-500', emoji: '📚' },
  financeiro: { label: 'Financeiro', color: 'bg-green-600', emoji: '💰' },
  pedagogico: { label: 'Pedagógico', color: 'bg-blue-600', emoji: '📖' },
  reclamacao: { label: 'Reclamação', color: 'bg-red-500', emoji: '⚠️' },
  elogio: { label: 'Elogio', color: 'bg-pink-500', emoji: '💖' },
  duvida: { label: 'Dúvida', color: 'bg-cyan-500', emoji: '❓' },
  sugestao: { label: 'Sugestão', color: 'bg-indigo-500', emoji: '💡' },
  outro: { label: 'Outro', color: 'bg-gray-500', emoji: '📝' }
};

const SENTIMENTO_CONFIG = {
  positivo: { label: 'Positivo', color: 'text-green-600', icon: ThumbsUp },
  neutro: { label: 'Neutro', color: 'text-gray-600', icon: MessageSquare },
  negativo: { label: 'Negativo', color: 'text-red-600', icon: ThumbsDown }
};

const PRIORIDADE_CONFIG = {
  baixa: { label: 'Baixa', color: 'bg-gray-400' },
  media: { label: 'Média', color: 'bg-yellow-500' },
  alta: { label: 'Alta', color: 'bg-orange-500' },
  urgente: { label: 'Urgente', color: 'bg-red-600' }
};

export default function DnaAtendimentoAI() {
  // Estados principais
  const [activeTab, setActiveTab] = useState('inbox');
  const [mensagens, setMensagens] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Filtros
  const [filtroStatus, setFiltroStatus] = useState('all');
  const [filtroCategoria, setFiltroCategoria] = useState('all');
  const [filtroPrioridade, setFiltroPrioridade] = useState('all');
  
  // Modal de mensagem
  const [mensagemSelecionada, setMensagemSelecionada] = useState(null);
  const [showMensagem, setShowMensagem] = useState(false);
  const [resposta, setResposta] = useState('');
  const [gerandoResposta, setGerandoResposta] = useState(false);
  
  // Modal de classificação manual
  const [showClassificar, setShowClassificar] = useState(false);
  const [textoClassificar, setTextoClassificar] = useState('');
  const [resultadoClassificacao, setResultadoClassificacao] = useState(null);
  
  // Carregar dados
  const carregarMensagens = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtroStatus !== 'all') params.append('status', filtroStatus);
      if (filtroCategoria !== 'all') params.append('categoria', filtroCategoria);
      if (filtroPrioridade !== 'all') params.append('prioridade', filtroPrioridade);
      
      const res = await api.get(`/growth/atendimento/mensagens?${params.toString()}`);
      setMensagens(res.data.mensagens || []);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  }, [filtroStatus, filtroCategoria, filtroPrioridade]);
  
  const carregarEstatisticas = useCallback(async () => {
    try {
      const res = await api.get('/growth/atendimento/estatisticas');
      setEstatisticas(res.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);
  
  useEffect(() => {
    carregarMensagens();
    carregarEstatisticas();
  }, [carregarMensagens, carregarEstatisticas]);
  
  // Handler de geração de resposta com IA
  const handleGerarResposta = async () => {
    if (!mensagemSelecionada) return;
    
    try {
      setGerandoResposta(true);
      const params = new URLSearchParams({
        mensagem: mensagemSelecionada.conteudo,
        categoria: mensagemSelecionada.categoria || 'duvida',
        tom: 'acolhedor'
      });
      
      const res = await api.post(`/growth/atendimento/gerar-resposta?${params.toString()}`);
      setResposta(res.data.resposta || '');
      toast.success('Resposta gerada pela IA!');
    } catch (error) {
      console.error('Erro ao gerar resposta:', error);
      toast.error('Erro ao gerar resposta');
    } finally {
      setGerandoResposta(false);
    }
  };
  
  // Handler de envio de resposta
  const handleEnviarResposta = async () => {
    if (!mensagemSelecionada || !resposta.trim()) {
      toast.error('Digite uma resposta');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        resposta: resposta,
        automatica: 'false'
      });
      
      await api.post(`/growth/atendimento/mensagem/${mensagemSelecionada.id}/responder?${params.toString()}`);
      toast.success('Resposta enviada!');
      setShowMensagem(false);
      setResposta('');
      carregarMensagens();
      carregarEstatisticas();
    } catch (error) {
      console.error('Erro ao enviar resposta:', error);
      toast.error('Erro ao enviar resposta');
    }
  };
  
  // Handler de arquivamento
  const handleArquivar = async (mensagemId) => {
    try {
      await api.post(`/growth/atendimento/mensagem/${mensagemId}/arquivar`);
      toast.success('Mensagem arquivada');
      carregarMensagens();
      carregarEstatisticas();
    } catch (error) {
      console.error('Erro ao arquivar:', error);
      toast.error('Erro ao arquivar mensagem');
    }
  };
  
  // Handler de classificação
  const handleClassificar = async () => {
    if (!textoClassificar.trim()) {
      toast.error('Digite o texto para classificar');
      return;
    }
    
    try {
      setLoading(true);
      const res = await api.post('/growth/atendimento/classificar', {
        conteudo: textoClassificar
      });
      setResultadoClassificacao(res.data);
      toast.success('Texto classificado!');
    } catch (error) {
      console.error('Erro ao classificar:', error);
      toast.error('Erro ao classificar texto');
    } finally {
      setLoading(false);
    }
  };
  
  // Componente de card de mensagem
  const MensagemCard = ({ mensagem }) => {
    const statusConfig = STATUS_CONFIG[mensagem.status] || STATUS_CONFIG.nao_lida;
    const categoriaConfig = CATEGORIA_CONFIG[mensagem.categoria] || CATEGORIA_CONFIG.outro;
    const sentimentoConfig = SENTIMENTO_CONFIG[mensagem.sentimento] || SENTIMENTO_CONFIG.neutro;
    const prioridadeConfig = PRIORIDADE_CONFIG[mensagem.prioridade] || PRIORIDADE_CONFIG.baixa;
    const SentimentoIcon = sentimentoConfig.icon;
    
    return (
      <Card 
        className={`cursor-pointer hover:shadow-md transition-shadow ${mensagem.status === 'nao_lida' ? 'border-l-4 border-l-blue-500' : ''}`}
        onClick={() => {
          setMensagemSelecionada(mensagem);
          setShowMensagem(true);
          setResposta(mensagem.resposta || '');
        }}
        data-testid={`mensagem-card-${mensagem.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {mensagem.rede_social === 'instagram' && <Instagram className="w-4 h-4 text-pink-500" />}
                {mensagem.tipo === 'direct' && <MessageCircle className="w-4 h-4 text-blue-500" />}
                {mensagem.tipo === 'email' && <Mail className="w-4 h-4 text-gray-500" />}
                <span className="font-medium">{mensagem.autor_nome || mensagem.autor_usuario || 'Anônimo'}</span>
                <Badge className={`${prioridadeConfig.color} text-white text-xs`}>
                  {prioridadeConfig.label}
                </Badge>
                {mensagem.requer_humano && (
                  <Badge className="bg-red-100 text-red-700 text-xs">
                    <Users className="w-3 h-3 mr-1" />
                    Requer Humano
                  </Badge>
                )}
              </div>
              
              <p className="text-sm text-gray-700 line-clamp-2 mb-2">{mensagem.conteudo}</p>
              
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${categoriaConfig.color} text-white text-xs`}>
                  {categoriaConfig.emoji} {categoriaConfig.label}
                </Badge>
                <span className={`flex items-center gap-1 text-xs ${sentimentoConfig.color}`}>
                  <SentimentoIcon className="w-3 h-3" />
                  {sentimentoConfig.label}
                </span>
                <Badge variant="outline" className="text-xs">
                  {statusConfig.label}
                </Badge>
              </div>
            </div>
            
            <div className="text-right text-xs text-gray-500">
              {new Date(mensagem.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-blue-600" />
            DNA Atendimento AI
          </h1>
          <p className="text-gray-500 text-sm">Classificação e Respostas Inteligentes</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setShowClassificar(true)} data-testid="btn-classificar">
            <Sparkles className="w-4 h-4 mr-2" />
            Classificar Texto
          </Button>
          
          <Button onClick={carregarMensagens} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>
      
      {/* Estatísticas */}
      {estatisticas && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <Inbox className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Não Lidas</p>
                  <p className="text-xl font-bold">{estatisticas.nao_lidas || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-100">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Urgentes</p>
                  <p className="text-xl font-bold">{estatisticas.urgentes || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-orange-100">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Requer Humano</p>
                  <p className="text-xl font-bold">{estatisticas.requer_humano || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-xl font-bold">{estatisticas.total_mensagens || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="inbox" className="flex items-center gap-2">
            <Inbox className="w-4 h-4" />
            Inbox
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
        </TabsList>
        
        {/* Tab Inbox */}
        <TabsContent value="inbox">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle>Mensagens</CardTitle>
                  <CardDescription>Comentários, DMs e mensagens para responder</CardDescription>
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {Object.entries(CATEGORIA_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.emoji} {config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select value={filtroPrioridade} onValueChange={setFiltroPrioridade}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Prioridade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {Object.entries(PRIORIDADE_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : mensagens.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Inbox className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma mensagem encontrada</p>
                  <p className="text-sm mt-2">As mensagens aparecerão aqui quando forem recebidas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {mensagens.map(mensagem => (
                    <MensagemCard key={mensagem.id} mensagem={mensagem} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tab Analytics */}
        <TabsContent value="analytics">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Por Categoria */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Por Categoria</CardTitle>
              </CardHeader>
              <CardContent>
                {estatisticas?.por_categoria ? (
                  <div className="space-y-3">
                    {Object.entries(estatisticas.por_categoria).map(([cat, count]) => {
                      const config = CATEGORIA_CONFIG[cat] || CATEGORIA_CONFIG.outro;
                      return (
                        <div key={cat} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={`${config.color} text-white`}>
                              {config.emoji}
                            </Badge>
                            <span>{config.label}</span>
                          </div>
                          <span className="font-medium">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Sem dados</p>
                )}
              </CardContent>
            </Card>
            
            {/* Por Sentimento */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Por Sentimento</CardTitle>
              </CardHeader>
              <CardContent>
                {estatisticas?.por_sentimento ? (
                  <div className="space-y-3">
                    {Object.entries(estatisticas.por_sentimento).map(([sent, count]) => {
                      const config = SENTIMENTO_CONFIG[sent] || SENTIMENTO_CONFIG.neutro;
                      const Icon = config.icon;
                      return (
                        <div key={sent} className="flex items-center justify-between">
                          <div className={`flex items-center gap-2 ${config.color}`}>
                            <Icon className="w-5 h-5" />
                            <span>{config.label}</span>
                          </div>
                          <span className="font-medium">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Sem dados</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Modal de Mensagem */}
      <Dialog open={showMensagem} onOpenChange={setShowMensagem}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {mensagemSelecionada && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {mensagemSelecionada.autor_nome || mensagemSelecionada.autor_usuario || 'Anônimo'}
                </DialogTitle>
                <DialogDescription>
                  {mensagemSelecionada.tipo} • {new Date(mensagemSelecionada.created_at).toLocaleString('pt-BR')}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Mensagem original */}
                <Card className="bg-gray-50">
                  <CardContent className="p-4">
                    <p className="text-gray-800">{mensagemSelecionada.conteudo}</p>
                  </CardContent>
                </Card>
                
                {/* Classificação */}
                {mensagemSelecionada.classificacao && (
                  <Card className="border-blue-200 bg-blue-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-blue-600" />
                        Análise da IA
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span>
                          <strong>Categoria:</strong>{' '}
                          <Badge className={`${CATEGORIA_CONFIG[mensagemSelecionada.categoria]?.color || 'bg-gray-500'} text-white`}>
                            {CATEGORIA_CONFIG[mensagemSelecionada.categoria]?.label || mensagemSelecionada.categoria}
                          </Badge>
                        </span>
                        <span>
                          <strong>Sentimento:</strong>{' '}
                          <span className={SENTIMENTO_CONFIG[mensagemSelecionada.sentimento]?.color}>
                            {SENTIMENTO_CONFIG[mensagemSelecionada.sentimento]?.label || mensagemSelecionada.sentimento}
                          </span>
                        </span>
                        <span>
                          <strong>Prioridade:</strong>{' '}
                          <Badge className={`${PRIORIDADE_CONFIG[mensagemSelecionada.prioridade]?.color || 'bg-gray-500'} text-white`}>
                            {PRIORIDADE_CONFIG[mensagemSelecionada.prioridade]?.label || mensagemSelecionada.prioridade}
                          </Badge>
                        </span>
                      </div>
                      {mensagemSelecionada.classificacao.resumo && (
                        <p><strong>Resumo:</strong> {mensagemSelecionada.classificacao.resumo}</p>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* Campo de resposta */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Resposta</Label>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleGerarResposta}
                      disabled={gerandoResposta}
                    >
                      {gerandoResposta ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Gerar com IA
                    </Button>
                  </div>
                  <Textarea 
                    placeholder="Digite sua resposta..."
                    value={resposta}
                    onChange={(e) => setResposta(e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => handleArquivar(mensagemSelecionada.id)}
                  className="w-full sm:w-auto"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Arquivar
                </Button>
                
                <Button 
                  onClick={handleEnviarResposta}
                  disabled={!resposta.trim()}
                  className="w-full sm:w-auto"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Resposta
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal de Classificação */}
      <Dialog open={showClassificar} onOpenChange={setShowClassificar}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Classificar Texto com IA
            </DialogTitle>
            <DialogDescription>
              Cole um texto para ver como a IA o classifica
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Texto para classificar</Label>
              <Textarea 
                placeholder="Cole aqui o texto de uma mensagem, comentário ou DM..."
                value={textoClassificar}
                onChange={(e) => setTextoClassificar(e.target.value)}
                rows={4}
              />
            </div>
            
            <Button 
              className="w-full"
              onClick={handleClassificar}
              disabled={loading || !textoClassificar.trim()}
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Classificar
            </Button>
            
            {/* Resultado */}
            {resultadoClassificacao && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Resultado da Classificação</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <strong>Categoria:</strong>{' '}
                      <Badge className={`${CATEGORIA_CONFIG[resultadoClassificacao.categoria]?.color || 'bg-gray-500'} text-white`}>
                        {CATEGORIA_CONFIG[resultadoClassificacao.categoria]?.label || resultadoClassificacao.categoria}
                      </Badge>
                    </div>
                    <div>
                      <strong>Sentimento:</strong>{' '}
                      <span className={SENTIMENTO_CONFIG[resultadoClassificacao.sentimento]?.color}>
                        {SENTIMENTO_CONFIG[resultadoClassificacao.sentimento]?.label || resultadoClassificacao.sentimento}
                      </span>
                    </div>
                    <div>
                      <strong>Prioridade:</strong>{' '}
                      <Badge className={`${PRIORIDADE_CONFIG[resultadoClassificacao.prioridade]?.color || 'bg-gray-500'} text-white`}>
                        {PRIORIDADE_CONFIG[resultadoClassificacao.prioridade]?.label || resultadoClassificacao.prioridade}
                      </Badge>
                    </div>
                    <div>
                      <strong>Requer Humano:</strong>{' '}
                      {resultadoClassificacao.requer_humano ? 'Sim' : 'Não'}
                    </div>
                  </div>
                  {resultadoClassificacao.resumo && (
                    <p><strong>Resumo:</strong> {resultadoClassificacao.resumo}</p>
                  )}
                  {resultadoClassificacao.motivo_humano && (
                    <p><strong>Motivo:</strong> {resultadoClassificacao.motivo_humano}</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
